import { useBaseCheckoutStore } from '@/composables/useBaseCheckoutStore'
import { STORAGE_KEYS } from '@/constants/CheckoutConstants'
import { useAddressesStore } from '@/stores/addresses'
import { usePaymentStore } from '@/stores/payment'
import { Address } from '@/types/address'
import {
  RemovableRef,
  StorageSerializers,
  useSessionStorage
} from '@vueuse/core'
import { defineStore } from 'pinia'

interface RegularCheckoutState {
  useSameBillingAddress: RemovableRef<boolean | null>
  selectedShippingAddressId: RemovableRef<number | null>
  selectedShippingAddressData: RemovableRef<Address | null>
  selectedBillingAddressId: RemovableRef<number | null>
  selectedBillingAddressData: RemovableRef<Address | null>
  selectedPaymentMethodId: RemovableRef<number | null>
  selectedPaymentMethodData: RemovableRef<object | null>
  blacklistedPaymentMethodIds: Array<number>
  selectedDeliveryOptionsMap: RemovableRef<object | null>
  subscribeToNewsletter: RemovableRef<boolean | null>
  phoneNumber: RemovableRef<string | null>
  ageData: RemovableRef<object | null>
  email: RemovableRef<string | null>
  storeAddressPaymentAsDefault: RemovableRef<boolean | null>
}

export const useRegularCheckoutStore = defineStore('regularCheckout', {
  state: (): RegularCheckoutState => ({
    useSameBillingAddress: useSessionStorage(
      STORAGE_KEYS.USE_SAME_BILLING_ADDRESS,
      null
    ),
    selectedShippingAddressId: useSessionStorage(
      STORAGE_KEYS.SELECTED_SHIPPING_ADDRESS_ID,
      null,
      { serializer: StorageSerializers.number }
    ),
    selectedShippingAddressData: useSessionStorage(
      STORAGE_KEYS.SELECTED_SHIPPING_ADDRESS_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    selectedBillingAddressId: useSessionStorage(
      STORAGE_KEYS.SELECTED_BILLING_ADDRESS_ID,
      null,
      { serializer: StorageSerializers.number }
    ),
    selectedBillingAddressData: useSessionStorage(
      STORAGE_KEYS.SELECTED_BILLING_ADDRESS_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    selectedPaymentMethodId: useSessionStorage(
      STORAGE_KEYS.SELECTED_PAYMENT_METHOD_ID,
      null,
      { serializer: StorageSerializers.number }
    ),
    selectedPaymentMethodData: useSessionStorage(
      STORAGE_KEYS.SELECTED_PAYMENT_METHOD_DATA,
      null,
      { serializer: StorageSerializers.object }
    ),
    blacklistedPaymentMethodIds: [],
    selectedDeliveryOptionsMap: useSessionStorage(
      STORAGE_KEYS.SELECTED_DELIVERY_OPTIONS_MAP,
      null,
      { serializer: StorageSerializers.object }
    ),
    storeAddressPaymentAsDefault: useSessionStorage(
      STORAGE_KEYS.STORE_ADDRESS_PAYMENT_AS_DEFAULT,
      false
    ),
    subscribeToNewsletter: useSessionStorage(
      STORAGE_KEYS.SUBSCRIBE_TO_NEWSLETTER,
      null
    ),
    phoneNumber: useSessionStorage(STORAGE_KEYS.PHONE_NUMBER, null),
    ageData: useSessionStorage(STORAGE_KEYS.AGE_DATA, null, {
      serializer: StorageSerializers.object
    }),
    email: useSessionStorage(STORAGE_KEYS.EMAIL, null)
  }),

  getters: {
    hasShippingAddress(): boolean {
      const { hasShippingAddress } = useBaseCheckoutStore(this)
      return hasShippingAddress.value
    },
    hasBillingAddress(): boolean {
      const { hasBillingAddress } = useBaseCheckoutStore(this)
      return hasBillingAddress.value
    },
    hasPaymentMethod(): boolean {
      const { hasPaymentMethod } = useBaseCheckoutStore(this)
      return hasPaymentMethod.value
    },
    getShippingAddressData(): Address | null {
      if (this.selectedShippingAddressData) {
        return this.selectedShippingAddressData
      }
      if (!this.selectedShippingAddressId) {
        return null
      }
      const addressesStore = useAddressesStore()
      const address = addressesStore.getAddressById(
        this.selectedShippingAddressId
      )
      return address ? (address as Address) : null
    },
    getBillingAddressData(): Address | null {
      if (this.selectedBillingAddressData) {
        return this.selectedBillingAddressData
      }
      if (!this.selectedBillingAddressId) {
        return null
      }
      const addressesStore = useAddressesStore()
      const address = addressesStore.getAddressById(
        this.selectedBillingAddressId
      )
      return address ? (address as Address) : null
    },
    getSelectedPaymentMethodData(): object | null {
      if (this.selectedPaymentMethodData) {
        return this.selectedPaymentMethodData
      }
      if (!this.selectedPaymentMethodId) {
        return null
      }
      const paymentStore = usePaymentStore()
      return (
        paymentStore.getPaymentMethodById(this.selectedPaymentMethodId) ?? null
      )
    }
  },

  actions: {
    clearData() {
      this.storeAddressPaymentAsDefault = null
      this.useSameBillingAddress = null
      this.selectedPaymentMethodData = null
      this.selectedPaymentMethodId = null
      this.selectedDeliveryOptionsMap = null
      this.selectedShippingAddressData = null
      this.selectedShippingAddressId = null
      this.selectedBillingAddressData = null
      this.selectedBillingAddressId = null
      this.email = null
      this.subscribeToNewsletter = null
      this.phoneNumber = null
      this.ageData = null
      this.blacklistedPaymentMethodIds = []
    },

    /**
     * Sets existing addresses and payment method as default.
     * Called by RegularCheckoutStrategy when storeAddressPaymentAsDefault is true.
     */
    async _setExistingAddressesAsDefault() {
      const addressesStore = useAddressesStore()
      const paymentStore = usePaymentStore()

      try {
        if (
          this.selectedShippingAddressId &&
          this.selectedBillingAddressId &&
          this.selectedShippingAddressId === this.selectedBillingAddressId &&
          !this.selectedShippingAddressData &&
          !this.selectedBillingAddressData
        ) {
          // Same address for shipping and billing
          const address = addressesStore.getAddressById(
            this.selectedShippingAddressId
          )
          if (address) {
            await addressesStore.updateAddress({
              ...address,
              is_default_shipping_address: true,
              is_default_billing_address: true
            })
          }
        } else {
          // Set shipping address as default
          if (
            this.selectedShippingAddressId &&
            !this.selectedShippingAddressData
          ) {
            const shippingAddress = addressesStore.getAddressById(
              this.selectedShippingAddressId
            )
            if (shippingAddress) {
              await addressesStore.updateAddress({
                ...shippingAddress,
                is_default_shipping_address: true
              })
            }
          }

          // Set billing address as default
          if (
            this.selectedBillingAddressId &&
            !this.selectedBillingAddressData &&
            this.selectedBillingAddressId !== this.selectedShippingAddressId
          ) {
            const billingAddress = addressesStore.getAddressById(
              this.selectedBillingAddressId
            )
            if (billingAddress) {
              await addressesStore.updateAddress({
                ...billingAddress,
                is_default_billing_address: true
              })
            }
          }
        }

        // Set payment method as default
        if (this.selectedPaymentMethodId && !this.selectedPaymentMethodData) {
          const paymentMethod = paymentStore.getPaymentMethodById(
            this.selectedPaymentMethodId
          )
          if (paymentMethod) {
            await paymentStore.updatePaymentMethod(
              this.selectedPaymentMethodId,
              {
                is_default_payment_method: true
              }
            )
          }
        }
      } catch (error) {
        console.error(
          'Error setting existing addresses/payment as default:',
          error
        )
      }
    }
  }
})
